import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Crown, MagnifyingGlass, Buildings, Desktop, Trash } from "@phosphor-icons/react";
import { PLAN_OPTS, PLAN_CLS, PLAN_LABEL, fmtDate } from "./adminUtils";

export function UsersTab({ users, loading, onSetPlan, onRemoveInternal, onDelete }) {
  const [query, setQuery] = useState("");
  const [emptyOnly, setEmptyOnly] = useState(false);

  const filtered = users.filter((u) =>
    (!query ||
      u.email?.toLowerCase().includes(query.toLowerCase()) ||
      u.name?.toLowerCase().includes(query.toLowerCase())) &&
    (!emptyOnly || (u.machines || 0) === 0)
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email"
            className="pl-9 bg-black border-zinc-800 focus-visible:ring-blue-600"
          />
        </div>
        <button
          data-testid="admin-filter-empty"
          onClick={() => setEmptyOnly((v) => !v)}
          className={`text-sm rounded-md px-3 h-10 border transition-colors ${emptyOnly ? "bg-blue-600 border-blue-600 text-white" : "bg-black border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}
        >
          0 machines only
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm animate-pulse">Loading users…</div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 bg-zinc-900/60 font-mono text-xs uppercase tracking-wider text-zinc-500">
            <div className="col-span-5">User</div>
            <div className="col-span-2 hidden sm:block">Machines</div>
            <div className="col-span-4 sm:col-span-3">Tier (primary workspace)</div>
            <div className="col-span-3 sm:col-span-2 text-right">Actions</div>
          </div>
          {filtered.map((u) => (
            <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="grid grid-cols-12 items-center px-5 py-4 border-b border-white/10 last:border-0 hover:bg-zinc-900/40 transition-colors">
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={u.picture} />
                  <AvatarFallback className="bg-zinc-800 text-xs">{u.name?.[0] || u.email?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {u.name || u.email}
                    {u.is_super_admin && <Crown size={13} weight="fill" className="text-amber-400 shrink-0" />}
                  </div>
                  <div className="font-mono text-xs text-zinc-500 truncate">{u.email}</div>
                </div>
              </div>
              <div className="col-span-2 hidden sm:flex items-center gap-1.5 text-sm text-zinc-400">
                <Desktop size={14} className={(u.machines || 0) === 0 ? "text-zinc-600" : "text-blue-400"} />
                <span className={(u.machines || 0) === 0 ? "text-zinc-600" : ""}>{u.machines || 0}</span>
                <span className="text-zinc-600 flex items-center gap-1 ml-2"><Buildings size={13} /> {u.workspaces?.length || 0}</span>
              </div>
              <div className="col-span-4 sm:col-span-3">
                {u.primary_org_id ? (
                  <div className="flex flex-col gap-1 items-start">
                    <Select value={u.plan || "personal"} onValueChange={(v) => onSetPlan(u, u.primary_org_id, v)}>
                      <SelectTrigger data-testid={`admin-plan-select-${u.user_id}`} className={`w-44 bg-black border-zinc-800 ${PLAN_CLS[u.plan] || ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {(PLAN_OPTS.includes(u.plan) || !u.plan ? PLAN_OPTS : [...PLAN_OPTS, u.plan]).map((p) => (
                          <SelectItem key={p} value={p}>{PLAN_LABEL[p] || p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {u.plan === "internal" && (
                      <div className="flex flex-col gap-0.5" data-testid={`admin-internal-info-${u.user_id}`}>
                        <span className="text-[10px] text-zinc-500">
                          Underlying: {PLAN_LABEL[u.base_plan] || u.base_plan}
                          <button data-testid={`admin-remove-internal-${u.user_id}`} onClick={() => onRemoveInternal(u, u.primary_org_id)} className="ml-2 text-fuchsia-400 hover:text-fuchsia-300 underline underline-offset-2">Remove Unlimited</button>
                        </span>
                        {u.has_stripe_subscription && (
                          <span data-testid={`admin-internal-stripe-warning-${u.user_id}`} className="text-[10px] text-amber-400">Stripe subscription still active — it keeps billing until cancelled in Stripe.</span>
                        )}
                      </div>
                    )}
                    {u.subscription_status === "canceled" && (
                      <span
                        data-testid={`admin-canceled-badge-${u.user_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide rounded px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20"
                        title={`Subscription canceled on ${fmtDate(u.canceled_at)}`}
                      >
                        Canceled · {fmtDate(u.canceled_at)}
                      </span>
                    )}
                    {u.subscription_status !== "canceled" && u.cancel_at_period_end && (
                      <span
                        data-testid={`admin-pending-cancel-badge-${u.user_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20"
                        title={`Cancels on ${fmtDate(u.cancel_at)}`}
                      >
                        Cancels · {fmtDate(u.cancel_at)}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-zinc-600">No workspace</span>
                )}
              </div>
              <div className="col-span-3 sm:col-span-2 flex justify-end">
                {u.is_super_admin ? (
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">Protected</span>
                ) : (
                  <button
                    data-testid={`admin-delete-user-${u.user_id}`}
                    onClick={() => onDelete(u)}
                    title="Delete user"
                    className="w-9 h-9 rounded-md flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-zinc-500">No users match your filters.</div>
          )}
        </div>
      )}
      <p className="text-xs text-zinc-600 mt-4 font-mono">Granting a tier here overrides payment. The workspace gets that plan's limits immediately.</p>
    </>
  );
}
